// React and hooks
import React, { useState, useMemo, useRef, useLayoutEffect, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSwipeable } from 'react-swipeable';

// Local hooks
import { useProfile } from '@/hooks/use-profile';

// Context
import { useUserConfig, useUserConfigDispatch } from '@/context/UserConfigContext';

// Validation
import { ValidationProvider, ValidationContext, ValidationFooter, useValidationToasts, mergeSchemas } from '@/components/validation';
import type { SchemaEntry } from '@/components/validation';
import {
  profileSchema,
  rolesSchema,
  premiumShiftSchema,
  multipliersSchema,
  billingSchema,
  mealBreakSchema,
  shortTurnSchema,
  holidaysSchema,
  overnightSchema,
  travelSchema,
  expenseSchema,
  invoiceSchema,
} from '@/utils/validation/schemas';

// Components
import { PageContainer } from '@/components/layout/PageContainer';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  ProfileConfig,
  RolesConfig,
  MultipliersConfig,
  InvoiceConfig,
  TravelConfig,
  UserExpenseConfig,
  UserGearConfig
} from '@/components/user_configuration';
import { UserSurchargeConfig } from '@/components/user_configuration/user_billing/UserSurchargeConfig';
import { BreakTimePolicySection } from '@/components/config_forms/breaks/BreakTimePolicySection';

// Types
import type { MultipliersConfigHandle } from '@/components/user_configuration/user_multipliers/UserMultipliersConfig';
import type { RolesConfigHandle } from '@/components/user_configuration/user_roles/UserRolesConfig';

// Utils
import { cn } from '@/lib/utils';
import { focusField } from '@/utils/focus';
import { CreditCard } from 'lucide-react';

/**
 * Merge multiple Zod schemas into a single schema that collects all issues.
 */

/** Map section keys to their scope keys in the schemaMap */
function getSectionScope(activeTab: string, activeSubTab: string): string {
  if (activeTab === 'contract') return activeSubTab;
  return activeTab;
}

/** Inner component that consumes ValidationContext. */
const ProfileContent = () => {
  const ctx = useContext(ValidationContext);
  if (!ctx) throw new Error('ProfileContent must be used within ValidationProvider');

  // Emit toast notifications for active errors
  useValidationToasts();

  // ----- dynamic height handling -----
  const contentRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();

  useLayoutEffect(() => {
    function updateHeight() {
      if (window.innerWidth >= 768 || !contentRef.current) return;
      const tabsHeight = 0;
      const navHeight  = 64;
      const extraPad   = 32;
    }

    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);
  const [activeSubTab, setActiveSubTab] = useState('roles');
  const multipliersRef = useRef<MultipliersConfigHandle>(null);
  const rolesRef = useRef<RolesConfigHandle>(null);
  const [multipliersStepValid, setMultipliersStepValid] = useState(true);
  const [rolesStepValid, setRolesStepValid] = useState(true);

  const {
    isSaving,
    isLoading,
    activeTab,
    setActiveTab,
    deltaX,
    setDeltaX,
    handleSave,
    direction,
    hasUnsavedChanges,
    revertChanges
  } = useProfile();

  const { state: userConfigState } = useUserConfig();
  const { updateBillingConfig } = useUserConfigDispatch();

  // Current scope key for validation
  const currentSectionKey = getSectionScope(activeTab, activeSubTab);

  // Combine schema validation with imperative-handle validity (unsaved inline edits)
  const schemaValid = ctx.isValid(currentSectionKey);
  const isValid = schemaValid && rolesStepValid && multipliersStepValid;

  // Track whether the user has attempted to save so footer hint only appears after
  const [hasAttemptedSave, setHasAttemptedSave] = useState(false);

  // Reset attempted-save flag when section changes
  useEffect(() => {
    setHasAttemptedSave(false);
  }, [currentSectionKey]);

  const tabsOrder = ['profile', 'invoicing', 'contract'];

  const swipeHandlers = useSwipeable({
    onSwiping: (event) => {
      if (window.innerWidth >= 768) return;
      if (Math.abs(event.deltaY) > Math.abs(event.deltaX)) return;

      const screenWidth = window.innerWidth;
      const maxSwipe = screenWidth * 0.8;
      const clampedDelta = Math.max(Math.min(event.deltaX, maxSwipe), -maxSwipe);
      setDeltaX(clampedDelta);
    },
    onSwiped: (event) => {
      if (window.innerWidth >= 768) return;

      const screenWidth = window.innerWidth;
      const swipeThreshold = screenWidth * 0.2;

      if (Math.abs(event.deltaX) > swipeThreshold) {
        const currentIndex = tabsOrder.indexOf(activeTab);
        if (event.deltaX < 0 && currentIndex < tabsOrder.length - 1) {
          const nxt = tabsOrder[currentIndex + 1];
          navigate(nxt === 'profile' ? '/profile' : `/profile/${nxt}`);
        } else if (event.deltaX > 0 && currentIndex > 0) {
          const prev = tabsOrder[currentIndex - 1];
          navigate(prev === 'profile' ? '/profile' : `/profile/${prev}`);
        }
      }
      setDeltaX(0);
    },
    trackMouse: true,
    preventScrollOnSwipe: false,
    delta: 10
  });

  // Keep URL and tabs in sync
  useEffect(() => {
    const seg =
      location.pathname.startsWith('/profile/contract')
        ? 'contract'
      : location.pathname.startsWith('/profile/invoicing')
        ? 'invoicing'
        : 'profile';
    if (seg !== activeTab) {
      if (hasUnsavedChanges) {
        revertChanges();
      }
      setActiveTab(seg);
    }
  }, [location.pathname, activeTab, setActiveTab, hasUnsavedChanges, revertChanges]);

  // Build footer hint text for sub-form blocking
  const subFormHint =
    !rolesStepValid ? 'Please save or cancel the open rate sheet form.'
    : !multipliersStepValid ? 'Please save or cancel the open multiplier form.'
    : null;

  const hasSchemaErrors = ctx.getActiveErrors(currentSectionKey).length > 0;
  const showFooterHint = (hasSchemaErrors || subFormHint) && hasAttemptedSave;

  return (
    <PageContainer
      title="Profile Settings"
      subtitle={
        activeTab === 'profile'
          ? "Manage your personal information"
          : activeTab === 'contract'
            ? activeSubTab === 'policies'
              ? "Configure your default work policies"
              : "Manage your rates, multipliers, and surcharges"
            : "Set up your default invoice settings"
      }
    >
      <div className="space-y-6">
        {isLoading ? (
          <div className="flex justify-center items-center h-[50vh]">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : (
          <>

          <div
            className={cn(
              "mt-2 relative z-10 flex-1",
              "pb-52 md:overflow-visible"
            )}
            {...swipeHandlers}
            ref={contentRef}
          >
            <Tabs value={activeTab}>
              <TabsContent value="profile" className="m-0">
                <div
                  className={`relative h-full ${direction === 'left' ? 'animate-page-slide-from-right' : 'animate-page-slide-from-left'}`}
                  style={{
                    transform: `translateX(${deltaX}px)`,
                    transition: deltaX !== 0 ? 'none' : 'transform 0.3s ease-out'
                  }}
                >
                  <ProfileConfig
                    firstName={userConfigState.firstName}
                    lastName={userConfigState.lastName}
                    email={userConfigState.email}
                    phone={userConfigState.phone}
                  />
                </div>
              </TabsContent>

              <TabsContent value="contract" className="m-0">
                <div
                  className={`relative ${direction === 'left' ? 'animate-page-slide-from-right' : 'animate-page-slide-from-left'}`}
                  style={{
                    transform: `translateX(${deltaX}px)`,
                    transition: deltaX !== 0 ? 'none' : 'transform 0.3s ease-out'
                  }}
                >
                  <Tabs value={activeSubTab} onValueChange={setActiveSubTab}>
                    <TabsList className="w-full grid grid-cols-3 equipment-panel rounded p-1 h-auto mb-4">
                      <TabsTrigger
                        value="roles"
                        className="font-heading uppercase tracking-[0.08em] text-xs sm:text-sm py-2.5 rounded-sm data-[state=active]:bg-[var(--primary)] data-[state=active]:text-[var(--color-surface-0)] data-[state=active]:shadow-sm text-muted-foreground"
                      >
                        Rate Sheet
                      </TabsTrigger>
                      <TabsTrigger
                        value="policies"
                        className="font-heading uppercase tracking-[0.08em] text-xs sm:text-sm py-2.5 rounded-sm data-[state=active]:bg-[var(--primary)] data-[state=active]:text-[var(--color-surface-0)] data-[state=active]:shadow-sm text-muted-foreground"
                      >
                        Policies
                      </TabsTrigger>
                      <TabsTrigger
                        value="gear"
                        className="font-heading uppercase tracking-[0.08em] text-xs sm:text-sm py-2.5 rounded-sm data-[state=active]:bg-[var(--primary)] data-[state=active]:text-[var(--color-surface-0)] data-[state=active]:shadow-sm text-muted-foreground"
                      >
                        Gear & Fees
                      </TabsTrigger>
                    </TabsList>

                    <TabsContent value="policies" className="mt-4 space-y-6 md:space-y-8">
                      <TravelConfig noCard />
                      <UserExpenseConfig />
                      <div className="equipment-panel rounded p-4 opacity-50 md:p-6">
                        <div className="flex items-center justify-between">
                          <h3 className="font-heading text-base uppercase tracking-[0.08em] text-foreground">Cancellation Window</h3>
                          <span className="font-heading text-[10px] uppercase tracking-[0.08em] bg-[var(--color-surface-2)] text-muted-foreground px-2 py-0.5 rounded">Coming Soon</span>
                        </div>
                        <p className="text-sm text-muted-foreground mt-2">
                          Set minimum notice requirements for shift cancellations and last-minute changes.
                        </p>
                      </div>
                      <div className="equipment-panel rounded p-4 opacity-50 md:p-6">
                        <div className="flex items-center justify-between">
                          <h3 className="font-heading text-base uppercase tracking-[0.08em] text-foreground">Split Shifts</h3>
                          <span className="font-heading text-[10px] uppercase tracking-[0.08em] bg-[var(--color-surface-2)] text-muted-foreground px-2 py-0.5 rounded">Coming Soon</span>
                        </div>
                        <p className="text-sm text-muted-foreground mt-2">
                          Configure policies for non-contiguous shifts for the same client in the same day.
                        </p>
                      </div>
                    </TabsContent>

                    <TabsContent value="roles" className="mt-4">
                      <div className="equipment-panel rounded p-4 md:p-6">
                        <div className="space-y-2 text-center">
                          <div className="flex items-center justify-center gap-3">
                            <CreditCard className="h-5 w-5 text-[var(--primary)]" />
                            <h3 className="font-heading text-xl uppercase tracking-[0.08em] text-foreground">Rate Card</h3>
                          </div>
                          <p className="text-sm text-muted-foreground px-2 max-w-lg mx-auto leading-relaxed">
                            Define your standard roles and business policies.<br></br>Acts as a template that can be modified per client, booking, or shift.
                          </p>
                        </div>

                        <RolesConfig
                          ref={rolesRef}
                          onValidityChange={setRolesStepValid}
                          checkForClientUsage={true}
                        />
                        <div className="mt-6 md:mt-8">
                          <MultipliersConfig
                            ref={multipliersRef}
                            onValidityChange={setMultipliersStepValid}
                          />
                        </div>
                        <div className="mt-6 md:mt-8">
                          <BreakTimePolicySection
                            breakBillingPolicy={userConfigState.mealConfig?.breakBillingPolicy}
                            onBreakBillingPolicyChange={(policy) => updateBillingConfig({ mealConfig: { ...userConfigState.mealConfig, breakBillingPolicy: policy } })}
                            noCard
                          />
                        </div>
                        <div className="mt-6 md:mt-8">
                          <UserSurchargeConfig noCard />
                        </div>
                      </div>
                    </TabsContent>

                    <TabsContent value="gear" className="mt-4 space-y-6 md:space-y-8">
                      <UserGearConfig />
                    </TabsContent>
                  </Tabs>
                </div>
              </TabsContent>

              <TabsContent value="invoicing" className="m-0">
                <div
                  className={`relative ${direction === 'left' ? 'animate-page-slide-from-right' : 'animate-page-slide-from-left'}`}
                  style={{
                    transform: `translateX(${deltaX}px)`,
                    transition: deltaX !== 0 ? 'none' : 'transform 0.3s ease-out'
                  }}
                >
                  <InvoiceConfig
                    paymentTermsDays={userConfigState.paymentTermsDays}
                    lateFeePercentage={userConfigState.lateFeePercentage}
                    lateFeeFixedAmount={userConfigState.lateFeeFixedAmount}
                    recurringLateFeeInterval={userConfigState.recurringLateFeeInterval}
                    recurringLateFee={userConfigState.recurringLateFee}
                    earlyPaymentDiscountPercentage={userConfigState.earlyPaymentDiscountPercentage}
                    earlyPaymentFixedAmount={userConfigState.earlyPaymentFixedAmount}
                    earlyPaymentDays={userConfigState.earlyPaymentDays}
                    useLateFee={userConfigState.useLateFee}
                    lateFeeType={userConfigState.lateFeeType}
                    useEarlyPaymentDiscount={userConfigState.useEarlyPaymentDiscount}
                    earlyPaymentType={userConfigState.earlyPaymentType}
                    autoSendReminders={userConfigState.autoSendReminders}
                    employmentType={userConfigState.employmentType}
                    reminderDays={userConfigState.reminderDays}
                    useCancellationPolicy={userConfigState.useCancellationPolicy}
                    cancellationPolicy={userConfigState.cancellationPolicy}
                    rushPremiumPolicy={userConfigState.rushPremiumPolicy}
                  />
                </div>
              </TabsContent>

            </Tabs>
          </div>

          {hasUnsavedChanges && (
            <div className={cn(
              "flex justify-end items-center gap-3",
              "fixed bottom-[74px] right-4 z-50",
              "md:bottom-6 md:right-6 md:bg-background/95 md:backdrop-blur md:rounded-lg md:shadow-lg md:p-2"
            )}>
              {showFooterHint && (
                <div className="max-w-[280px] min-w-[120px]">
                  {subFormHint ? (
                    <p className="font-sans text-xs text-destructive text-center leading-tight">
                      {subFormHint}
                    </p>
                  ) : (
                    <ValidationFooter scope={currentSectionKey} />
                  )}
                </div>
              )}
              <span
                className="shrink-0"
                onClick={() => {
                  if (!isValid) {
                    setHasAttemptedSave(true);
                    ctx.triggerShake();
                    const errors = ctx.getActiveErrors(currentSectionKey);
                    if (errors.length > 0) {
                      setTimeout(() => focusField(errors[0].field), 100);
                    }
                  }
                }}
              >
                <Button
                  onClick={async () => {
                    const currentSection =
                      activeTab === 'contract'
                        ? activeSubTab
                        : activeTab;
                    try {
                      await handleSave(currentSection);
                    } catch (_) {
                      /* failure is already logged inside handleSave / saveUserConfig */
                    }
                  }}
                  disabled={isSaving || !isValid}
                  className={cn(
                    "min-w-[120px] font-heading uppercase tracking-[0.08em]",
                    "shadow-md bg-[var(--primary)] text-[var(--color-surface-0)] hover:bg-[var(--color-primary-hover)]",
                    "md:shadow-none",
                    "active:translate-y-[1px]",
                    !isValid && "opacity-50 cursor-not-allowed"
                  )}
                >
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </Button>
              </span>
            </div>
          )}
          </>
        )}
      </div>
    </PageContainer>
  );
};

/** Outer component: builds schemaMap from state and wraps in ValidationProvider. */
const ProfilePageOuter: React.FC = () => {
  const { state } = useUserConfig();

  const schemaMap: Record<string, SchemaEntry> = useMemo(() => ({
    'profile': {
      schema: profileSchema,
      data: state,
    },
    'roles': {
      schema: mergeSchemas(
        rolesSchema,
        premiumShiftSchema,
        multipliersSchema,
        billingSchema,
        mealBreakSchema,
        shortTurnSchema,
        holidaysSchema,
        overnightSchema,
      ),
      data: state,
    },
    'policies': {
      schema: mergeSchemas(travelSchema, expenseSchema),
      data: state,
    },
    'invoicing': {
      schema: invoiceSchema,
      data: state,
    },
  }), [state]);

  return (
    <ValidationProvider
      showToasts={true}
      showSuccessFeedback={true}
      schemaMap={schemaMap}
    >
      <ProfileContent />
    </ValidationProvider>
  );
};

const ProfilePage: React.FC = () => <ProfilePageOuter />;

export default ProfilePage;
